import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditMedicalNotesContent } from './content'
import { EditMedicalNotesControls } from './controls'

export enum Input {
  Allergies = 'allergies',
  MedicalNotes = 'medical_notes',
}

export const editMedicalNotesSchema = z.object({
  [Input.Allergies]: z.string()
    .max(2048, { message: 'Allergies must be less than 2048 characters.' })
    .optional(),
  [Input.MedicalNotes]: z.string()
    .max(2048, { message: 'Medical notes must be less than 2048 characters.' })
    .optional(),
})

export type EditMedicalNotesSchema = z.infer<typeof editMedicalNotesSchema>

type EditMedicalNotesFormContextType = {
  onSubmit: (values: EditMedicalNotesSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditMedicalNotesFormContext = createContext<EditMedicalNotesFormContextType | undefined>(undefined)

type EditMedicalNotesFormProps = EditMedicalNotesFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditMedicalNotesSchema>,
}

export function EditMedicalNotesForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditMedicalNotesFormProps) {

  const context: EditMedicalNotesFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditMedicalNotesFormContext.Provider value={context}>
      <Form<EditMedicalNotesSchema, typeof editMedicalNotesSchema>
        schema={editMedicalNotesSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditMedicalNotesFormContext.Provider>
  )
}

EditMedicalNotesForm.Content = EditMedicalNotesContent
EditMedicalNotesForm.Controls = EditMedicalNotesControls

export function useEditMedicalNotesForm() {
  const context = useContext(EditMedicalNotesFormContext)

  if (context === undefined) {
    throw new Error('useEditMedicalNotesForm must be used within an EditMedicalNotesFormProvider')
  }

  return context
}
